import CustomWidgetModifierOperators from 'ContentUtils/constants/CustomWidgetModifierOperators';
import { BrightnessOffsetModifierTypename, ShadeModifierTypename } from '../../__generated__/chirp/com/hubspot/global/styles/rpc/GlobalStylesRpc';
/** Leaf FE→CHIRP: maps a `modifier_type` string enum to a `__typename`-tagged CHIRP modifier union member. */
const fieldModifierToChirpModifier = modifier => {
  if (modifier.modifier_type === CustomWidgetModifierOperators.SHADE) {
    return {
      __typename: ShadeModifierTypename,
      modifierValue: modifier.modifier_value
    };
  }
  if (modifier.modifier_type === CustomWidgetModifierOperators.BRIGHTNESS_OFFSET) {
    return {
      __typename: BrightnessOffsetModifierTypename,
      modifierValue: Number(modifier.modifier_value)
    };
  }
  return undefined;
};

/** Leaf CHIRP→FE: maps a `__typename`-tagged CHIRP modifier back to the FE `modifier_type` + `modifier_value` pair. */
const chirpModifierToFieldModifier = modifier => {
  if (modifier.__typename === ShadeModifierTypename) {
    return {
      modifier_type: CustomWidgetModifierOperators.SHADE,
      modifier_value: modifier.modifierValue
    };
  }
  if (modifier.__typename === BrightnessOffsetModifierTypename) {
    return {
      modifier_type: CustomWidgetModifierOperators.BRIGHTNESS_OFFSET,
      modifier_value: modifier.modifierValue
    };
  }
  return undefined;
};

/** Single value FE→CHIRP: wraps `property_value_paths` + modifier into CHIRP's nested `bindingConfig` envelope. Empty input → empty `{}` (unbound). */
const inheritedValueToFieldConfig = value => {
  const {
    property_value_paths,
    modifier
  } = value;
  if (!property_value_paths) {
    return {};
  }
  const bindingConfig = {
    propertyValuePaths: property_value_paths
  };
  const chirpModifier = modifier && fieldModifierToChirpModifier(modifier);
  if (chirpModifier) {
    bindingConfig.modifier = chirpModifier;
  }
  return {
    bindingConfig
  };
};

/** Single value CHIRP→FE: unwraps CHIRP's `bindingConfig` back to the flat FE `InheritedValue` shape. Empty input → empty `{}` (unbound). */
const fieldConfigToInheritedValue = config => {
  const {
    bindingConfig
  } = config;
  if (!bindingConfig) return {};
  const result = {
    property_value_paths: bindingConfig.propertyValuePaths
  };
  const fieldModifier = bindingConfig.modifier && chirpModifierToFieldModifier(bindingConfig.modifier);
  if (fieldModifier) {
    result.modifier = fieldModifier;
  }
  return result;
};

/** Map-level CHIRP→FE: converts a record of CHIRP `FieldConfig` into a record of FE `InheritedValue`, keyed by field path. */
export const fieldConfigsToBindings = configs => {
  const bindings = {};
  for (const [fieldPath, config] of Object.entries(configs)) {
    bindings[fieldPath] = fieldConfigToInheritedValue(config);
  }
  return bindings;
};

/** Map-level FE→CHIRP: converts a record of FE `InheritedValue` into a record of CHIRP `FieldConfig`, keyed by field path. */
export const bindingsToFieldConfigs = bindings => {
  const configs = {};
  for (const [fieldPath, binding] of Object.entries(bindings)) {
    configs[fieldPath] = inheritedValueToFieldConfig(binding);
  }
  return configs;
};